import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { ApiRequestError } from '@/shared/api/api-error';

/**
 * The application's TanStack Query client.
 *
 * Defaults chosen for a planning UI whose freshness rules are explicit (R13):
 * server data is cached and re-derived across orientation changes, but an
 * authentication or authorization failure is never retried — a 401 or 403 is a
 * state the error boundary must surface immediately, not a transient the client
 * should hammer. Other failures retry once.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30 * 1000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          if (error instanceof ApiRequestError) {
            if (error.code === 'unauthenticated' || error.code === 'forbidden') {
              return false;
            }
          }
          return failureCount < 1;
        },
      },
      mutations: {
        retry: false,
      },
    },
  });
}

export function QueryProvider({ children }: { children: ReactNode }): React.JSX.Element {
  // One client per app instance, created lazily so it is stable across renders.
  const [client] = useState(createQueryClient);
  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
}
